<script setup>
import { isNil, isNull } from 'lodash'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { BUTTON_SIZES_NEXT, BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { APP_MODAL_SIZES } from '@/utils/components-configurations/app-modal'
import { formatDate } from '@/utils/date'
import {
  AI_REPORT_GENERATION_STATUSES,
  AI_REPORT_KEYS,
  AI_REPORT_RESPONSE_KEYS
} from '@/utils/home-page/ai-report'
import { objectOrNullProp } from '@/utils/prop-validators'

import HomePageAlignmentReport from '@/components/home-page/HomePageAlignmentReport'
import {
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_EMPTY_STATE_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_FILTERS_INTERVAL_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_FILTERS_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_GENERATED_AT_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_GENERATING_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_RECALCULATE_BUTTON_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_SELECTION_NAME_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_SPINNER_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_STATUS_MESSAGE_TEST_ID,
  HOMEPAGE_ALIGNMENT_REPORT_DIALOG_TEST_ID
} from '@/components/home-page/jest-helpers'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import AppModal from '@/components/ui/AppModal/AppModal'

defineOptions({
  name: 'HomePageAlignmentReportDialog'
})

const props = defineProps({
  selectionName: {
    type: String,
    default: ''
  },

  // the canGenerate, generationStatus and report of the Alignment type, null while none is served
  reportSection: {
    validator: v => objectOrNullProp(v),
    default: null
  },

  // the generate request is in flight or the server reports the generation as running (FR-0.12)
  isGenerating: {
    type: Boolean
  },

  nothingToAnalyse: {
    type: Boolean
  },

  // the names of the Home filters that apply to the report, for the line of FR-0.27
  intervalName: {
    type: String,
    default: ''
  },

  workspaceName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({
  recalculate: null
})

const show = defineModel('show', {
  type: Boolean
})

const { t } = useI18n()

const { FAILED, TIMED_OUT } = AI_REPORT_GENERATION_STATUSES

const DIALOG_ICON_SIZE = 24

// the spinner of the generating state (screens 1.1.2 and 2.1.3): a light track ring and an open
// arc of three quarters with a purple-to-blue gradient, drawn as an SVG because the UI kit's
// LoadingCircle has neither the track nor the gradient
const SPINNER_SIZE = 84
const SPINNER_STROKE_WIDTH = 8
const SPINNER_CENTER = SPINNER_SIZE / 2
const SPINNER_RADIUS = SPINNER_CENTER - SPINNER_STROKE_WIDTH / 2
const SPINNER_CIRCUMFERENCE = 2 * Math.PI * SPINNER_RADIUS
const SPINNER_ARC_LENGTH = SPINNER_CIRCUMFERENCE * 0.75
const SPINNER_VIEW_BOX = `0 0 ${SPINNER_SIZE} ${SPINNER_SIZE}`
const SPINNER_DASH_ARRAY = `${SPINNER_ARC_LENGTH} ${SPINNER_CIRCUMFERENCE}`
const SPINNER_GRADIENT_ID = 'hpard-spinner-gradient'
const SPINNER_ARC_STROKE = `url(#${SPINNER_GRADIENT_ID})`

const report = computed(() => props.reportSection?.[AI_REPORT_RESPONSE_KEYS.REPORT] ?? null)

const hasReport = computed(() => !isNull(report.value))

const canGenerate = computed(() => {
  return Boolean(props.reportSection?.[AI_REPORT_RESPONSE_KEYS.CAN_GENERATE])
})

const generatedAt = computed(() => {
  const value = report.value?.[AI_REPORT_KEYS.GENERATED_AT]

  return isNil(value) ? '' : formatDate(value)
})

const STATUS_MESSAGE_KEYS = {
  [FAILED]: 'homepage.ai_reports.failed',
  [TIMED_OUT]: 'homepage.ai_reports.timed_out'
}

// the failed, timed-out and nothing-to-analyse texts stand above the kept report (FR-0.13, FR-0.30, FR-1.11)
const statusMessage = computed(() => {
  if (props.nothingToAnalyse) {
    return t('homepage.ai_reports.nothing_to_analyse')
  }

  const messageKey =
    STATUS_MESSAGE_KEYS[props.reportSection?.[AI_REPORT_RESPONSE_KEYS.GENERATION_STATUS]]

  return messageKey ? t(messageKey) : ''
})

// "No report yet." for a reader who cannot generate and has no kept report (FR-0.4)
const showEmptyState = computed(() => !hasReport.value && !statusMessage.value)

// "Recalculate" belongs to a kept report and to a user with generate access, and leaves the
// footer while a generation runs (FR-0.5, FR-0.12, FR-0.18)
const showRecalculateButton = computed(() => {
  return hasReport.value && canGenerate.value && !props.isGenerating
})

const onClose = () => {
  show.value = false
}
</script>

<template>
  <AppModal
    :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_TEST_ID"
    :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_TEST_ID"
    :show="show"
    :size="APP_MODAL_SIZES.XL"
    class="hpard-Dialog"
    close-button-icon="close-next"
    @on-close="onClose"
  >
    <template #title>
      <div class="hpard-Title">
        <AppIcon
          :height="DIALOG_ICON_SIZE"
          :width="DIALOG_ICON_SIZE"
          class="hpard-Title_Icon"
          icon-name="magic-wand-gradient"
        />
        <span class="hpard-Title_Text">{{ t('homepage.ai_reports.alignment.dialog_title') }}</span>
        <template v-if="selectionName">
          <AppDivider class="hpard-Title_Divider" no-margin vertical />
          <span
            :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_SELECTION_NAME_TEST_ID"
            :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_SELECTION_NAME_TEST_ID"
            class="hpard-Title_Selection oboard-truncated-text"
          >
            {{ selectionName }}
          </span>
        </template>
      </div>
    </template>

    <div
      v-if="isGenerating"
      :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_GENERATING_TEST_ID"
      :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_GENERATING_TEST_ID"
      class="hpard-Generating"
    >
      <svg
        :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_SPINNER_TEST_ID"
        :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_SPINNER_TEST_ID"
        :height="SPINNER_SIZE"
        :viewBox="SPINNER_VIEW_BOX"
        :width="SPINNER_SIZE"
        class="hpard-Spinner"
        focusable="false"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient :id="SPINNER_GRADIENT_ID" x1="0" x2="1" y1="0" y2="1">
            <stop class="hpard-Spinner_GradientStart" offset="0" />
            <stop class="hpard-Spinner_GradientEnd" offset="1" />
          </linearGradient>
        </defs>
        <circle
          :cx="SPINNER_CENTER"
          :cy="SPINNER_CENTER"
          :r="SPINNER_RADIUS"
          :stroke-width="SPINNER_STROKE_WIDTH"
          class="hpard-Spinner_Track"
        />
        <circle
          :cx="SPINNER_CENTER"
          :cy="SPINNER_CENTER"
          :r="SPINNER_RADIUS"
          :stroke="SPINNER_ARC_STROKE"
          :stroke-dasharray="SPINNER_DASH_ARRAY"
          :stroke-width="SPINNER_STROKE_WIDTH"
          class="hpard-Spinner_Arc"
        />
      </svg>
      <div class="hpard-Generating_Title">
        {{ t('homepage.ai_reports.alignment.generating.title') }}
      </div>
      <div class="hpard-Generating_Description">
        {{ t('homepage.ai_reports.alignment.generating.description') }}
      </div>
      <div class="hpard-Generating_Hint">
        {{ t('homepage.ai_reports.alignment.generating.hint') }}
      </div>
    </div>

    <div v-else class="hpard-Body">
      <div
        v-if="statusMessage"
        :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_STATUS_MESSAGE_TEST_ID"
        :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_STATUS_MESSAGE_TEST_ID"
        class="hpard-StatusMessage"
      >
        {{ statusMessage }}
      </div>

      <div
        v-if="showEmptyState"
        :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_EMPTY_STATE_TEST_ID"
        :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_EMPTY_STATE_TEST_ID"
        class="hpard-EmptyState"
      >
        {{ t('homepage.ai_reports.alignment.no_report_yet') }}
      </div>

      <template v-if="hasReport">
        <div class="hpard-Meta">
          <!-- the Interval name stands out of the filters line (screens 1.1.3, 2.1.2) -->
          <i18n-t
            :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_FILTERS_TEST_ID"
            :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_FILTERS_TEST_ID"
            class="hpard-Meta_Filters"
            keypath="homepage.ai_reports.alignment.filters_line"
            scope="global"
            tag="span"
          >
            <template #interval>
              <span
                :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_FILTERS_INTERVAL_TEST_ID"
                :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_FILTERS_INTERVAL_TEST_ID"
                class="hpard-Meta_Interval"
              >
                {{ intervalName }}
              </span>
            </template>
            <template #workspace>{{ workspaceName }}</template>
          </i18n-t>
          <span
            v-if="generatedAt"
            :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_GENERATED_AT_TEST_ID"
            :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_GENERATED_AT_TEST_ID"
            class="hpard-Meta_GeneratedAt"
          >
            {{ t('homepage.ai_reports.generated_at', { date: generatedAt }) }}
          </span>
        </div>

        <HomePageAlignmentReport :report="report" />
      </template>
    </div>

    <template v-if="showRecalculateButton" #footer>
      <div class="hpard-Footer">
        <AppButtonNext
          :data-auto-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_RECALCULATE_BUTTON_TEST_ID"
          :data-testid="HOMEPAGE_ALIGNMENT_REPORT_DIALOG_RECALCULATE_BUTTON_TEST_ID"
          :size="BUTTON_SIZES_NEXT.XL"
          :variant="BUTTON_VARIANTS.PRIMARY"
          class="hpard-Footer_Button"
          @click="emit('recalculate')"
        >
          {{ t('homepage.ai_reports.alignment.recalculate') }}
        </AppButtonNext>
      </div>
    </template>
  </AppModal>
</template>

<style lang="scss" scoped>
.hpard-Dialog {
  font-family: $system-ui;

  // the title bar is closed by a thin line across the dialog (screens 1.1.2 to 3.1.2) and the
  // footer of a kept report is a light-grey band under one (screens 2.1.2 and 3.1.2); AppModal
  // reads no CSS variable for its header and footer, so their nodes are reached the way the
  // other modals of the project do
  &:deep(.o-modal-header) {
    border-bottom: 1px solid $grey-2-next;
  }

  &:deep(.o-modal-footer) {
    border-top: 1px solid $grey-2-next;
    border-radius: 0 0 $border-radius-md $border-radius-md;
    background-color: $grey-3-next;
  }
}

.hpard-Title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  color: $dark-1;
}

// the wand carries its blue-to-purple gradient in the sprite icon itself (screens 1.1.2 to 3.1.2)
.hpard-Title_Icon {
  flex-shrink: 0;
}

.hpard-Title_Text {
  flex-shrink: 0;
  font-size: $fs-20;
  font-weight: fw('bold');
  line-height: 28px;
}

.hpard-Title_Divider {
  height: 24px;
}

.hpard-Title_Selection {
  color: $dark-2;
  font-size: $fs-20;
  font-weight: fw('regular');
  line-height: 28px;
}

.hpard-Generating {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 420px;
  padding: 40px 24px;
  border-radius: $border-radius-md-next;
  // the pastel blend of the generating screens: soft green, lavender, blue and yellow blobs
  background: radial-gradient(ellipse 45% 55% at 30% 45%, rgba($green-1, 0.14), transparent 70%),
    radial-gradient(ellipse 40% 50% at 78% 22%, rgba($purple, 0.14), transparent 70%),
    radial-gradient(ellipse 50% 45% at 68% 85%, rgba($blue-7, 0.14), transparent 70%),
    radial-gradient(ellipse 35% 40% at 50% 60%, rgba($orange-1, 0.1), transparent 70%), $white;
  text-align: center;
}

.hpard-Spinner {
  flex-shrink: 0;
  margin-bottom: 24px;
  animation: hpard-spin 1s linear infinite;
}

.hpard-Spinner_Track {
  fill: none;
  stroke: rgba($blue-9, 0.16);
}

.hpard-Spinner_Arc {
  fill: none;
  stroke-linecap: round;
  transform: rotate(-90deg);
  transform-origin: center;
}

.hpard-Spinner_GradientStart {
  stop-color: $purple;
}

.hpard-Spinner_GradientEnd {
  stop-color: $blue-9;
}

@keyframes hpard-spin {
  to {
    transform: rotate(360deg);
  }
}

.hpard-Generating_Title {
  color: $dark-1;
  font-size: $fs-20;
  font-weight: fw('bold');
  line-height: 28px;
}

.hpard-Generating_Description {
  color: $dark-2;
  font-size: $fs-16;
  line-height: 24px;
}

.hpard-Generating_Hint {
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}

.hpard-Body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 8px;
}

.hpard-StatusMessage {
  padding: 8px 12px;
  border-radius: $border-radius-sm-next;
  background-color: $grey-3;
  color: $dark-1;
  font-size: $fs-14;
  line-height: 20px;
}

.hpard-EmptyState {
  padding: 40px 0;
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
  text-align: center;
}

.hpard-Meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
}

.hpard-Meta_Filters {
  color: $dark-3;
  font-size: $fs-14;
  line-height: 20px;
}

.hpard-Meta_Interval {
  color: $dark-1;
  font-weight: fw('bold');
}

.hpard-Meta_GeneratedAt {
  color: $dark-3;
  font-size: $fs-12;
  line-height: 16px;
}

.hpard-Footer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.hpard-Footer_Button {
  min-width: 170px;
}
</style>
